/** Persistent, owner-only entry point for social updates (ticket 67). */
import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, HttpError, type SocialActivityItem, type SocialActivityList } from '../api';
import { currentLang, errorText, t } from '../i18n';
import { backGlyph, tPart } from '../bidi-html';
import { go } from '../router';
import { subscribeLive } from '../live';
import { nameOf } from './raid/raid-bits';

@customElement('activity-view')
export class ActivityView extends LitElement {
  @state() private list?: SocialActivityList;
  @state() private loading = true;
  @state() private error = '';
  @state() private opening = '';
  private unsubscribe?: () => void;
  /** Newest load owns the state: a stale response resolving late writes nothing (ticket 92). */
  private loadSeq = 0;

  protected createRenderRoot() { return this; }

  async connectedCallback() {
    super.connectedCallback();
    // The live subscription is registered even when the first read fails, so
    // a digest change still refreshes a failed screen; the live callback
    // never rejects (an unhandled rejection would escape the poller).
    if (this.isConnected) this.unsubscribe = subscribeLive(() => this.load().catch(() => {}));
    try {
      const health = await api.health();
      if (!health.beta) { this.settleIfNoLoad(); go('/', true); return; }
      await this.load();
    } catch (e) {
      // Once any load has started it owns the error (its own failure is
      // already written); a late health failure must not cover its list.
      if (this.loadSeq === 0) this.error = errorText(e);
      this.settleIfNoLoad();
    }
  }

  /** Only a load clears `loading` (the newest one); with none started, nobody else will. */
  private settleIfNoLoad() {
    if (this.loadSeq === 0) this.loading = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  private async load() {
    const mine = ++this.loadSeq;
    const owned = () => mine === this.loadSeq;
    try {
      const list = await api.activity();
      // A newer load started meanwhile (live reload over the first read):
      // its response is the truth, so this stale one writes nothing.
      if (!owned()) return;
      this.list = list;
      this.error = '';
    } catch (e) {
      // A stale failure is not the screen's truth either: callers must not
      // show it over a newer load's state.
      if (!owned()) return;
      if (!this.list) this.error = errorText(e);
      throw e;
    } finally {
      if (owned()) this.loading = false;
    }
  }

  private describe(item: SocialActivityItem) {
    const name = nameOf(item.name ?? '');
    switch (item.kind) {
      case 'friend_request': return tPart('activity.friendRequest', 'name', name);
      case 'incoming_army': return tPart('activity.army', 'name', name, { count: item.count ?? 0 });
      case 'battle_report': return tPart('activity.report', 'name', name, { band: t(`raid.band.${item.band ?? 'held'}`) });
      case 'spoils': return tPart(item.state === 'resolved' ? 'activity.spoilsDone' : item.ready ? 'activity.spoilsReady' : 'activity.spoilsWaiting', 'name', name);
    }
  }

  private stateText(item: SocialActivityItem) {
    if (item.state !== 'available') return t(`activity.${item.state}`);
    return t(item.openedAt ? 'activity.seen' : 'activity.unread');
  }

  private actionText(item: SocialActivityItem) {
    return t(item.state === 'available' ? 'activity.open' : 'activity.view');
  }

  private outcomeText(item: SocialActivityItem) {
    return item.outcome ? t(`activity.outcome.${item.outcome}`) : '';
  }

  private async open(item: SocialActivityItem) {
    if (this.opening) return;
    this.opening = item.id;
    this.error = '';
    const destination = item.destination;
    try {
      // Discovery is recorded here. The destination's domain action is the only thing
      // that resolves the item, so merely opening it leaves actionable items available.
      await api.activityOpened(item.id);
      go(destination);
    } catch (e) {
      if (e instanceof HttpError && e.code === 'activity-not-found') {
        // The row is gone elsewhere (accepted, withdrawn, expired): the list
        // below is the truth, so reload it instead of following a dead link.
        try {
          await this.load();
          if (!this.list) this.error = errorText(e);
        } catch (reload) {
          this.error = errorText(reload);
        }
      } else {
        this.error = errorText(e);
      }
    } finally {
      this.opening = '';
    }
  }

  private date(at: string) {
    return new Date(at).toLocaleString(currentLang(), { dateStyle: 'medium', timeStyle: 'short' });
  }

  render() {
    const items = this.list?.items ?? [];
    return html`<main class="screen activity-screen" data-state=${this.loading ? 'loading' : this.list ? 'ready' : 'error'}>
      <style>
        .activity-screen{max-width:680px;margin:0 auto;padding:18px 16px 40px}
        .activity-intro{margin:6px 0 16px}
        .activity-list{display:grid;gap:10px}
        .activity-item{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px 14px;align-items:center}
        .activity-copy{min-width:0}
        .activity-state,.activity-date,.activity-outcome{display:block;margin-top:4px}
        .activity-open{white-space:nowrap}
        @media(max-width:420px){.activity-item{grid-template-columns:minmax(0,1fr)}.activity-open{width:100%}}
      </style>
      <div class="topbar">
        <button class="btn ghost icon" @click=${() => go('/')} aria-label=${t('common.homeAria')}>${backGlyph()}</button>
        <h1 style="font-size:17px">${t('activity.title')}</h1>
        <span></span>
      </div>
      <p class="muted activity-intro">${t('activity.intro')}</p>
      ${this.error ? html`<p class="circle-error" role="alert">${this.error}</p>` : nothing}
      ${this.loading
        ? html`<p class="muted">${t('circle.loading')}</p>`
        : !this.list
          ? nothing
          : items.length
            ? html`<section class="activity-list" aria-label=${t('activity.title')}>
                ${items.map((item) => html`<article class="card activity-item" data-id=${item.id} data-kind=${item.kind} data-state=${item.state}>
                  <div class="activity-copy">
                    <strong class="activity-description">${this.describe(item)}</strong>
                    <span class="muted small activity-state">${this.stateText(item)}</span>
                    ${this.outcomeText(item) ? html`<span class="muted small activity-outcome">${this.outcomeText(item)}</span>` : nothing}
                    ${item.indicatorShownAt ? html`<span class="muted small activity-indicator">${t('activity.indicatorShown')}</span>` : nothing}
                    <span class="muted small activity-date">${t('activity.availableAt', { date: this.date(item.availableAt) })}</span>
                  </div>
                  <button class="btn ${item.state === 'available' ? 'primary' : 'ghost'} activity-open" ?disabled=${!!this.opening}
                    @click=${() => this.open(item)}>${this.actionText(item)}</button>
                </article>`)}
              </section>`
            : html`<p class="muted card activity-empty" role="status">${t('activity.empty')}</p>`}
    </main>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'activity-view': ActivityView }
}
